import { useMemo, useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

// "Suggest an edit" — pick a person, see their record, propose one change.
//
// Nothing here writes to the directory. A suggestion is routed to whoever owns
// that field (HR, Operations or IT) and they make the change at source; the
// modal says so at every step, because a form that looks like an edit form and
// silently doesn't edit anything is worse than no form.
//
// Deliberately one field per suggestion: each field has a different owner, so a
// multi-field submission would need splitting into separate approvals and
// separate emails anyway.

// Fields where a free-text box invites typos and drift. Options are derived
// from values already in use rather than hardcoded, so the list follows the
// data instead of going stale — and "something else" is always available,
// because a genuinely new region has to be enterable.
const DROPDOWN_FIELDS = new Set(['region', 'cluster', 'isp', 'department', 'reports_to']);

function PersonPicker({ people, onSelect }) {
  const [search, setSearch] = useState('');
  const matches = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return people.slice(0, 8);
    return people
      .filter((p) => `${p.display_name || ''} ${p.email || ''} ${p.job_title || ''}`.toLowerCase().includes(t))
      .slice(0, 12);
  }, [people, search]);

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">Who is this about?</label>
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or job title..."
          className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>
      <ul className="mt-2 max-h-64 overflow-y-auto rounded-md border border-gray-200">
        {matches.map((p) => (
          <li key={p.entra_user_id}>
            <button
              type="button"
              onClick={() => onSelect(p)}
              className="flex w-full flex-col items-start border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-brand-light"
            >
              <span className="text-sm font-medium text-gray-900">{p.display_name}</span>
              <span className="text-xs text-gray-500">{p.job_title || '—'} · {p.email || '—'}</span>
            </button>
          </li>
        ))}
        {matches.length === 0 ? (
          <li className="px-3 py-6 text-center text-sm text-gray-400">Nobody matches that.</li>
        ) : null}
      </ul>
    </div>
  );
}

function SuggestEditModal({ open, onClose, people, columns, columnLabels, sources, displayOf, onSubmit }) {
  const [person, setPerson] = useState(null);
  const [fieldName, setFieldName] = useState('');
  const [newValue, setNewValue] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  // Suggestions are only meaningful for fields that come from a source someone
  // can correct — and the entra id is a join key, not information.
  const editable = columns.filter((c) => c !== 'entra_user_id' && sources[c]);

  const options = useMemo(() => {
    if (!DROPDOWN_FIELDS.has(fieldName)) return null;
    return [...new Set(people.map((p) => displayOf(fieldName, p[fieldName])).filter((v) => v && v !== '?'))].sort(
      (a, b) => String(a).localeCompare(String(b))
    );
  }, [people, fieldName, displayOf]);

  if (!open) return null;

  const currentRaw = person && fieldName ? person[fieldName] : '';
  const owner = fieldName ? sources[fieldName] : null;

  const reset = () => {
    setPerson(null); setFieldName(''); setNewValue(''); setReason('');
    setError(null); setDone(null); setSubmitting(false);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await onSubmit({
        subject: { entra_user_id: person.entra_user_id, display_name: person.display_name },
        field: { name: fieldName, label: columnLabels[fieldName] || fieldName },
        currentValue: currentRaw ?? '',
        suggestedValue: newValue,
        reason,
      });
      setDone(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit that suggestion.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="font-medium text-gray-900">Suggest an edit</h2>
          <button type="button" onClick={close} className="rounded p-1 text-gray-400 hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="px-5 py-8 text-center">
            <CheckCircleIcon className="mx-auto mb-3 h-12 w-12 text-emerald-600" />
            <p className="font-medium text-gray-900">Suggestion sent for approval</p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
              Once approved, {done.owner?.team || 'the owning team'} will be asked to make the change in{' '}
              {done.owner?.source || 'the source system'}. The directory updates after the next sync.
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4 px-5 py-4">
            {!person ? (
              <PersonPicker people={people} onSelect={setPerson} />
            ) : (
              <>
                <div className="flex items-start justify-between rounded-md bg-gray-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{person.display_name}</p>
                    <p className="text-xs text-gray-500">{person.job_title || '—'} · {person.email || '—'}</p>
                  </div>
                  <button type="button" onClick={() => { setPerson(null); setFieldName(''); setNewValue(''); }} className="text-xs text-gray-500 underline">
                    Change
                  </button>
                </div>

                {/* Their whole record, so a suggestion is made against what's
                    actually recorded rather than what someone remembers.
                    No height cap: with the trimmed field list every row fits,
                    and the previous max-h-48 pushed Contact — the field people
                    most often want to correct — below the fold of a scroll box
                    with no visible affordance, so it looked absent entirely. */}
                <div className="overflow-y-auto rounded-md border border-gray-200">
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-gray-100">
                      {editable.map((c) => (
                        <tr
                          key={c}
                          className={`cursor-pointer hover:bg-brand-light ${fieldName === c ? 'bg-brand-light' : ''}`}
                          onClick={() => { setFieldName(c); setNewValue(''); }}
                        >
                          <td className="px-3 py-1.5 text-gray-500">{columnLabels[c] || c}</td>
                          <td className="px-3 py-1.5 text-gray-900">{String(displayOf(c, person[c]) ?? '') || '—'}</td>
                          <td className="px-3 py-1.5 text-right text-[10px] text-gray-400">{sources[c]?.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {fieldName ? (
                  <>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        New value for {columnLabels[fieldName] || fieldName}
                      </label>
                      {options ? (
                        <>
                          <select
                            value={options.includes(newValue) ? newValue : ''}
                            onChange={(e) => setNewValue(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                          >
                            <option value="">Choose an existing value...</option>
                            {options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                          <input
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            placeholder="...or type something new"
                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                          />
                        </>
                      ) : (
                        <input
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                        />
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Currently <span className="font-medium text-gray-700">{String(displayOf(fieldName, currentRaw) ?? '') || '(empty)'}</span>
                        {owner ? <> · owned by <span className="font-medium text-gray-700">{owner.source}</span></> : null}
                      </p>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Why? (optional)</label>
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        placeholder="e.g. promoted in April, confirmed with their manager"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                      />
                    </div>

                    {owner ? (
                      <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        This does not change the data. If approved, {owner.fixIn ? `it will be actioned in ${owner.fixIn}` : 'the owning team will be asked to make the change'}.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-gray-400">Pick the field you want to correct.</p>
                )}
              </>
            )}

            {error ? <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}

            <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
              <button type="button" onClick={close} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!person || !fieldName || !newValue.trim() || submitting}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Submit for approval'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SuggestEditModal;
